import { CircularProgress, Container, makeStyles } from "@material-ui/core";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { findValueInEnum, maybe } from "@saleor/misc";
import { SmopStatusEnum } from "@saleor/types/globalTypes";
import { StoreTypeEnum } from "@saleor/types/globalTypes";
import React, { Fragment, useState } from "react";
import { useIntl } from "react-intl";

import { BusinessDetailsForm } from "./../../components/BusinessDetailsForm";
import Validate from "./../../controllers/CustomValidation";
import { BrandUpdateMutation } from "./../../mutations";
import { BrandDetails } from "./../../types/BrandDetails";
// import { useBrandQuery } from "./../../queries";
import { BrandUpdate } from "./../../types/BrandUpdate";
import { BusinessPageFormData } from "./../BusinessDetailsPage";

const useStyles = makeStyles(
  {
    bdloader: {
      alignItems: "center",
      backgroundColor: "rgba(255, 255, 255, .5)",
      bottom: 0,
      display: "flex",
      justifyContent: "center",
      left: 0,
      position: "fixed",
      right: 0,
      top: 0,
      zIndex: 99
    },
    frmAction: {
      justifyContent: "center"
    },
    frmBtn: {
      "&.active": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      color: "#0030FF",
      padding: "6px 30px"
    },
    frmTxt: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "18px",
      justifyContent: "center",
      minHeight: "100px",
      textAlign: "center"
    }
  },
  { name: "BusinessUpdatePage" }
);

interface BrandUpdatePageProp {
  brand: BrandDetails;
  onBack: () => void;
}

export const BusinessUpdatePage: React.FC<BrandUpdatePageProp> = ({
  brand,
  onBack
}) => {
  const classes = useStyles({});
  const notify = useNotifier();
  const intl = useIntl();
  const { user } = useUser();
  const [validations, setValidation] = useState({
    err: {},
    err_msg: []
  });
  const initialForm: BusinessPageFormData = {
    bankAccountName:
      (brand && brand.brandPrivate && brand.brandPrivate.bankAccountName) || "",
    bankAccountNo:
      (brand && brand.brandPrivate && brand.brandPrivate.bankAccountNo) || "",
    bankName:
      (brand && brand.brandPrivate && brand.brandPrivate.bankName) || "",
    completeAddress:
      (brand && brand.brandPrivate && brand.brandPrivate.completeAddress) || "",
    financeEmail:
      (brand && brand.brandPrivate && brand.brandPrivate.financeEmail) || "",
    financeMobileNo:
      (brand && brand.brandPrivate && brand.brandPrivate.financeMobileNo) || "",
    financeName:
      (brand && brand.brandPrivate && brand.brandPrivate.financeName) || "",
    legalName:
      (brand && brand.brandPrivate && brand.brandPrivate.legalName) || "",
    name: (brand && brand.brandPrivate && brand.brandPrivate.name) || "",
    signatoryDesignation:
      (brand &&
        brand.brandPrivate &&
        brand.brandPrivate.signatoryDesignation) ||
      "",
    signatoryEmail:
      (brand && brand.brandPrivate && brand.brandPrivate.signatoryEmail) || "",
    signatoryMobileNo:
      (brand && brand.brandPrivate && brand.brandPrivate.signatoryMobileNo) ||
      "",
    signatoryName:
      (brand && brand.brandPrivate && brand.brandPrivate.signatoryName) || "",
    storeType:
      (brand && brand.brandPrivate && brand.brandPrivate.storeType) || "",
    swornDeclarationsFlag: false,
    tinNo: (brand && brand.brandPrivate && brand.brandPrivate.tinNo) || ""
  };
  const [loader, setLoader] = useState(false);
  const handleBrandUpdate = (data: BrandUpdate) => data;
  return (
    <BrandUpdateMutation onCompleted={handleBrandUpdate}>
      {brandUpdate => {
        const handleSubmit = async (data: BusinessPageFormData) => {
          setLoader(true);
          setValidation({
            err: {},
            err_msg: []
          });
          const initValidations = {
            bankAccountName: { required: true, usename: "Bank Account Name" },
            bankAccountNo: { required: true, usename: "Bank Account No" },
            bankName: { required: true, usename: "Bank Name" },
            financeEmail: {
              email: true,
              required: true,
              usename: "Finance Email"
            },
            financeMobileNo: { required: true, usename: "Finance Mobile No" },
            financeName: { required: true, usename: "Finance Name" },
            legalName: { required: true, usename: "Company Name" },
            name: { required: true, usename: "Lease Name" },
            signatoryEmail: {
              email: true,
              required: true,
              usename: "Signatory Email"
            },
            signatoryMobileNo: {
              required: true,
              usename: "Signatory Mobile No"
            },
            signatoryName: { required: true, usename: "Signatory Name" },
            storeType: {
              required: true,
              usename: "Store Type"
            },
            tinNo: { required: true, usename: "Tin No" }
          };
          //   console.log(data);
          const {
            validation_errors,
            validation_has_error,
            validation_message
          } = await Validate(initValidations, data);
          if (validation_has_error) {
            const timer = setTimeout(() => {
              setValidation({
                err: validation_errors,
                err_msg: validation_message
              });
              notify({
                status: "error",
                text: "Invalid Form! Please complete the fields"
              });
              setLoader(false);
              return false;
            }, 1500);
            return () => clearTimeout(timer);
          }
          const brand_result = await brandUpdate({
            variables: {
              id: brand.brandPrivate.id,
              input: {
                attachTo: user.id,
                bankAccountName: data.bankAccountName,
                bankAccountNo: data.bankAccountNo,
                bankName: data.bankName,
                completeAddress: data.completeAddress,
                financeEmail: data.financeEmail,
                financeMobileNo: data.financeMobileNo,
                financeName: data.financeName,
                legalName: data.legalName,
                name: data.name,
                signatoryDesignation: data.signatoryDesignation,
                signatoryEmail: data.signatoryEmail,
                signatoryMobileNo: data.signatoryMobileNo,
                signatoryName: data.signatoryName,
                status: maybe(() => findValueInEnum("RVW", SmopStatusEnum)),
                storeType: maybe(() =>
                  findValueInEnum(data.storeType, StoreTypeEnum)
                ),
                tinNo: data.tinNo
              }
            }
          });
          const { errors } = brand_result.data.brandUpdate;
          if (errors && errors.length) {
            setLoader(false);
            notify({
              status: "error",
              text: "Invalid Form! Please complete the fields"
            });
            return false;
          }
          setLoader(false);

          notify({
            status: "success",
            text: intl.formatMessage(commonMessages.successBusinessDetails)
          });
          return null;
        };
        return (
          <Fragment>
            {loader && (
              <div className={classes.bdloader}>
                <CircularProgress size={150} />
              </div>
            )}

            <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
              {({ data, change, submit, hasChanged }) => (
                <Container>
                  <Grid>
                    <BusinessDetailsForm
                      validations={validations}
                      data={data}
                      onChange={change}
                    />
                  </Grid>

                  <div>
                    <SaveButtonBar
                      // state={brandUpdateOpts.status}
                      state={undefined}
                      onCancel={onBack}
                      onSave={submit}
                      disabled={!hasChanged}
                    />
                  </div>
                </Container>
              )}
            </Form>
          </Fragment>
        );
      }}
    </BrandUpdateMutation>
  );
};
export default BusinessUpdatePage;
